<template>
  <div>
    <CommonBreadCrumbs :data="breadcrumbs"></CommonBreadCrumbs>

    <div class="text-center mb-[52px] mt-10">
      <h1>{{ clinicData?.display_title }}</h1>
    </div>

    <div class="md:mt-0 mt-4 md:relative md:overflow-hidden">
      <div class="bg-secondary-lighter flex md:flex-row flex-col w-full md:h-[640px]">
        <div class="md:w-[40%] md:h-[640px] relative h-[400px] shrink-0 bg-secondary-lighter">
          <ClientOnly>
            <ClinicGoogleMap
              :clinicData="clinicData"
              :isMobile="isMobile"
              :showClinicLocation="true"
              :citySlug="clinicData?.city?.slug"
              :cityTitle="clinicData?.city?.title"
              :showGoogleMap="true"
              :range="15"
              :mapheight="isMobile ? '400px' : '640px'"
            />
            <template #fallback>
              <div class="w-full h-full animate-pulse bg-gray-200"></div>
            </template>
          </ClientOnly>
        </div>

        <div class="md:w-[60%] md:px-20 py-8 px-5 flex flex-col">
          <div class="text-space-cadet text-base leading-7 mb-10">
            {{ clinicData?.description }}
          </div>

          <div class="max-w-[90%] md:max-w-[80%] w-full mx-5 md:mx-auto">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-y-8 mb-6">
              <div v-if="clinicData?.show_address" class="flex flex-col gap-2">
                <div class="flex items-center gap-1 font-semibold text-space-cadet text-base">
                  <IconsMarkerPinAlt width="12" height="15" />
                  {{ $t("clinic.detail.address_title") }}
                </div>
                <div class="pl-6 text-space-cadet text-sm">
                  <ol class="list-none m-0 p-0">
                    <li>{{ clinicData?.address }}</li>
                    <li>{{ clinicData?.city?.title }},</li>
                    <li>{{ clinicData?.postcode }}</li>
                  </ol>
                </div>
              </div>

              <div class="flex flex-col gap-2">
                <div class="flex items-center gap-1 font-semibold text-space-cadet text-base">
                  <Icon name="Phone" :scale="1" class="w-5 h-5" />
                  {{ $t("clinic.detail.phone_title") }}
                </div>
                <div class="pl-6 text-sm">
                  <a :href="`tel:${clinicData?.telephone}`" class="text-secondary font-semibold hover:underline block">
                    {{ clinicData?.telephone }}
                  </a>
                  <p class="text-[10px] text-space-cadet font-medium">(Lines Open 24/7)</p>
                </div>
              </div>
            </div>

            <div v-if="clinicData?.features?.length > 0" class="mb-8 py-6">
              <div class="flex items-center gap-1 font-semibold text-[#213756F2] text-base mb-4">
                <IconsStar width="15" height="14.27" />
                {{ $t("clinic.detail.features_title") }}
              </div>
              <ul class="grid grid-cols-1 md:grid-cols-2 gap-y-2 w-full">
                <li
                  v-for="(feature, index) in clinicData?.features"
                  :key="index"
                  class="flex items-center gap-2 text-space-cadet text-sm leading-tight"
                >
                  <Icon name="Checkmark" :scale="1" />
                  <span>{{ feature }}</span>
                </li>
              </ul>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 mt-8 mb-6">
              <div class="md:col-span-2 flex justify-center md:justify-start md:ml-25">
                <BaseButton
                  :text="$t('clinic.detail.book_appointment')"
                  class="w-full md:w-[280px]"
                  @buttonClicked="bookAppointment"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <CommonSupport pageName="checkout" />

    <NuxtLayout name="center">
      <CommonLiveChatBlock class="md:mb-[130px]" />
    </NuxtLayout>

    <NuxtLayout name="center" :mobilePadding="false">
      <ProductReview
        class="mt-20 md:mb-[130px]"
        :reviewList="reviewResult?.data"
        :totalReview="reviewResult?.meta"
        totalReviewPosition="top"
      ></ProductReview>
    </NuxtLayout>

    <ClientOnly>
      <CommonPanel :showModal="showModal" />
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import { useGlobalStore } from "@/stores/global";
import { useMapStore } from "@/stores/map";
import { type PropType } from "vue";
import { useProductStore } from "~/stores/filter";

interface ClinicData {
  id: number;
  display_title: string;
  description: string;
  address: string;
  postcode: string;
  telephone: string;
  show_address: boolean;
  features: string[];
  slug: string;
  city: {
    title: string;
    slug: string;
  };
  latitude: string;
  longitude: string;
}

const props = defineProps({
  clinicData: {
    type: Object as PropType<ClinicData>,
    required: true,
  },
});

const globalStore = useGlobalStore();
const mapStore = useMapStore();
const productStore = useProductStore();
const route = useRoute();
const isMobile = useScreenSize("sm");
const breadcrumbs = ref();
const localePath = useLocalePath();

const breadcrumbsData: Record<string, string> = {
  city: props.clinicData?.city?.title,
  citySlug: props.clinicData?.city?.slug,
  title: props.clinicData?.display_title,
};
breadcrumbs.value = await getBreadcrumbs("clinic-detail", breadcrumbsData);

const { data: reviewResult } = await useBlockReview(1);

const filterOptions = await useFilterOptions();
productStore.setFilterOptions(filterOptions);

const showModal = computed(() => !!(route.query && (route.query.slug || route.query.appointment_booking === "true")));

const initBookingData = async () => {
  mapStore.setSelectedClinic({
    id: props.clinicData?.id,
    title: props.clinicData?.display_title,
    slug: props.clinicData?.slug,
  });

  const { data: servicesResult } = await useServiceListType();
  const availableServices = (servicesResult.value.data || [])
    .filter((service: any) => service.slug !== "clinic-test")
    .map((service: any) => service.slug);

  const { data: productsList } = await useFetchClinicTests(props.clinicData.id, "clinic-test", availableServices);
  if (productsList.value) {
    mapStore.getTests(productsList.value.data);
  }
};

if (showModal.value) {
  await initBookingData();
}

const bookAppointment = async () => {
  await initBookingData();

  mapStore.openPanel();

  navigateTo(
    localeUrlQuery(
      {
        query: {
          ...route.query,
          appointment_booking: "true",
        },
      },
      globalStore.siteCurrentLocale || ""
    )
  );
};
</script>
